import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { PromptInputSection } from './PromptInputSection';
import { TemplateBuilder } from './TemplateBuilder';
import { PreviewSection } from './PreviewSection';
import { fossilFillsApi } from '../../services/fossilFillsApi';
import { theme } from '../../styles/theme';

interface FossilTemplate {
  template: string;
  blanks: BlankDefinition[];
  domain: string;
  educational_notes: string[];
  original_prompt: string;
  basic_improvement: string;
}

interface BlankDefinition {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  required: boolean;
  help_text: string;
  position: number;
  original_text?: string;
}

interface FossilFillsState {
  stage: 'input' | 'filling' | 'preview';
  originalPrompt: string;
  template: FossilTemplate | null;
  filledBlanks: Record<string, string>;
  isLoading: boolean;
  error: string | null;
}

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 ${theme.spacing.md};
  }
`;

const StageIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${theme.spacing['2xl']};
  gap: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.xl};
    gap: 2px;
  }
`;

const Stage = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: ${props => 
    props.active ? theme.colors.primary : 
    props.completed ? theme.colors.success : 
    theme.colors.surface};
  color: ${props => 
    props.active || props.completed ? 'white' : 
    theme.colors.text.muted};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${props => props.active ? '600' : '500'};
  transition: all 0.3s ease;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.fontSizes.xs};
    gap: ${theme.spacing.xs};
    
    span:not(:first-child) {
      display: ${props => props.active ? 'inline' : 'none'};
    }
  }
`;

const StageNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  font-size: ${theme.fontSizes.xs};
`;

const StageLine = styled.div`
  width: 40px;
  height: 2px;
  background: ${theme.colors.border};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 20px;
  }
`;

export const FossilFillsContainer: React.FC = () => {
  const [state, setState] = useState<FossilFillsState>({
    stage: 'input',
    originalPrompt: '',
    template: null,
    filledBlanks: {},
    isLoading: false,
    error: null
  });

  const handlePromptSubmit = useCallback(async (prompt: string) => {
    setState(prev => ({ 
      ...prev, 
      isLoading: true, 
      originalPrompt: prompt,
      error: null 
    }));
    
    try {
      const response = await fossilFillsApi.createTemplate(prompt);
      
      // Initialize filled blanks - leave empty to encourage interaction
      // Only pre-fill the first 1-2 blanks as examples
      const initialBlanks: Record<string, string> = {};
      response.fossil_template.blanks.forEach((blank, index) => {
        // Pre-fill only the first blank as an example
        if (index === 0 && blank.suggestions && blank.suggestions.length > 0) {
          // Use the first suggestion as an example
          initialBlanks[blank.id] = blank.suggestions[0];
        }
        // Leave all other blanks empty to encourage dropdown interaction
      });
      
      setState(prev => ({
        ...prev,
        template: response.fossil_template,
        filledBlanks: initialBlanks,
        stage: 'filling',
        isLoading: false
      }));
    } catch (error) {
      console.error('Error creating template:', error);
      // Show the actual error message to help debug
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: `Failed to create template: ${errorMessage}`
      }));
    }
  }, []);

  const handleBlankFill = useCallback((blankId: string, value: string) => {
    setState(prev => ({
      ...prev,
      filledBlanks: { ...prev.filledBlanks, [blankId]: value }
    }));
  }, []);

  const handleComplete = useCallback(() => {
    setState(prev => ({ ...prev, stage: 'preview' }));
  }, []);

  const handleEdit = useCallback(() => {
    setState(prev => ({ ...prev, stage: 'filling' }));
  }, []);

  const handleReset = useCallback(() => {
    setState({
      stage: 'input',
      originalPrompt: '',
      template: null,
      filledBlanks: {},
      isLoading: false,
      error: null
    });
  }, []);

  const handleStartOver = useCallback(() => {
    // Go back to input stage but preserve the original prompt
    setState(prev => ({
      ...prev,
      stage: 'input',
      template: null,
      filledBlanks: {},
      error: null
    }));
  }, []);

  const handleDomainChange = useCallback(async (newDomain: string) => {
    if (!state.template || state.isLoading) return;
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Re-fetch template with the new domain specified
      const response = await fossilFillsApi.createTemplate(state.originalPrompt, newDomain);
      
      // Preserve existing filled blanks where possible
      const preservedBlanks: Record<string, string> = {};
      response.fossil_template.blanks.forEach(blank => {
        // If we had a value for a blank with the same ID, preserve it
        if (state.filledBlanks[blank.id]) {
          preservedBlanks[blank.id] = state.filledBlanks[blank.id];
        } else if (blank.suggestions && blank.suggestions.length > 0) {
          // Only pre-fill the first blank as an example for new templates
          if (Object.keys(preservedBlanks).length === 0) {
            preservedBlanks[blank.id] = blank.suggestions[0];
          }
        }
      });
      
      setState(prev => ({
        ...prev,
        template: response.fossil_template,
        filledBlanks: preservedBlanks,
        isLoading: false
      }));
    } catch (error) {
      console.error('Failed to update domain:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to update domain. Please try again.',
        isLoading: false
      }));
    }
  }, [state.template, state.originalPrompt, state.filledBlanks, state.isLoading]);

  return (
    <Container>
      <StageIndicator>
        <Stage active={state.stage === 'input'} completed={state.stage !== 'input'}>
          <StageNumber>1</StageNumber>
          <span>Input Prompt</span>
        </Stage>
        <StageLine />
        <Stage 
          active={state.stage === 'filling'} 
          completed={state.stage === 'preview'}
        >
          <StageNumber>2</StageNumber>
          <span>Fill Blanks</span>
        </Stage>
        <StageLine />
        <Stage active={state.stage === 'preview'} completed={false}>
          <StageNumber>3</StageNumber>
          <span>Preview & Copy</span>
        </Stage>
      </StageIndicator>

      <AnimatePresence mode="wait">
        {state.stage === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <PromptInputSection
              onSubmit={handlePromptSubmit}
              isLoading={state.isLoading}
              error={state.error}
            />
          </motion.div>
        )}

        {state.stage === 'filling' && state.template && (
          <motion.div
            key="filling"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <TemplateBuilder
              template={state.template}
              filledBlanks={state.filledBlanks}
              onBlankFill={handleBlankFill}
              onComplete={handleComplete}
              onDomainChange={handleDomainChange}
              onStartOver={handleStartOver}
            />
          </motion.div>
        )}

        {state.stage === 'preview' && state.template && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <PreviewSection
              template={state.template}
              filledBlanks={state.filledBlanks}
              originalPrompt={state.originalPrompt}
              onEdit={handleEdit}
              onReset={handleReset}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};